import * as THREE from 'three';

/**
 * 軌道 + エイム追従カメラ（z-up）。
 * - orbit(): 1本指/ドラッグで方位角・仰角を自由に回す。
 * - alignToAim(angle): 狙っている間、ショット方向が画面奥へ来るよう方位角を追従
 *   （tailuge の aimView と同じ考え方: 手球の -shotdir 側にカメラを置く）。
 * - zoom(): ホイール/ピンチ。
 * OrbitControls は aim 追従ができないため自前。標準的な球面座標カメラ。
 */
export interface CameraRig {
  orbit: (dAzimuth: number, dElevation: number) => void;
  zoom: (factor: number) => void;
  alignToAim: (angle: number) => void;
  setAimTarget: (x: number, y: number, z: number) => void;
  setFraming: (aiming: boolean) => void;
  /** 再生中に半径を拡大する（引きカメラ）。true=1.5倍、false=元倍率。滑らかに補間。
   *  ただしユーザーが orbit/zoom した後は active=true の呼び出しを無視する（手動優先）。 */
  setCinema: (active: boolean) => void;
  /** 俯瞰モード（再生中に球が画面外へ出た時の全体表示）。
   *  ユーザー手動操作後の active=true 呼び出しは無視。 */
  setOverview: (active: boolean) => void;
  /** ダイナミック・フォローフレーミング（ショット後・非トップビュー）。手球・的球・
   *  ポケットへ向かう球を必ず画面に収め、極端な引きを上限でキャップする。
   *  ユーザー手動操作後の active=true は無視（手動優先）。 */
  setFollow: (active: boolean) => void;
  /** フォローフレーミングの対象点を毎フレーム供給する。
   *  primary=必ず収める / secondary=余裕があれば収める。 */
  setFollowFrame: (
    primary: ReadonlyArray<{ x: number; y: number }>,
    secondary: ReadonlyArray<{ x: number; y: number }>,
  ) => void;
  /** 真上トップビュー（持続）。ユーザー操作では解除されず、再度 false で解除。
   *  source='manual'（既定）はユーザー明示の手動ロック、'auto' はシステム由来の自動ロック
   *  （例: 「対象球が隠れたら自動トップ」）。`isAutoTopActive()` は 'auto' のみを「自動」と判定。 */
  setTopViewLocked: (active: boolean, source?: 'manual' | 'auto') => void;
  /** 真上トップビュー（ショット後自動）。ユーザー手動操作後の active=true 呼び出しは無視。 */
  setAutoTopView: (active: boolean) => void;
  /** 現在「自動」トップビュー中か（HUDの自動半透明スキップ判定などに使う）。
   *  setAutoTopView(true) 中、または setTopViewLocked(true, 'auto') 中に true。
   *  手動ロック（source='manual'）の場合は false。 */
  isAutoTopActive: () => boolean;
  /** トップビューのフィット計算に使うテーブル外周半幅（railOuter）。 */
  setTopFitExtents: (halfX: number, halfY: number) => void;
  /** トップビュー中のピンチズーム。factor=pinchDist/dist（<1で拡大）。(ndcX,ndcY)=指の中点。
   *  その地点を固定したまま拡大縮小する（ズーム・トゥ・ポイント）。非トップビューでは無視。 */
  topPinch: (factor: number, ndcX: number, ndcY: number) => void;
  /** トップビュー拡大中の1本指パン（指の下の点が追従）。非トップビュー/未拡大では無視。 */
  topPan: (prevNdcX: number, prevNdcY: number, ndcX: number, ndcY: number) => void;
  /** トップビューのズーム/パンを既定（全体フィット・中心）へ戻す（ショット実行時など）。 */
  resetTopZoom: () => void;
  /** 現在トップビュー表示中か（ピンチ/パンの入力切替判定用）。 */
  isTopView: () => boolean;
  /** 現在のトップビュー拡大率（1=全体フィット）。1本指パン可否の判定に使う。 */
  getTopZoom: () => number;
  /** オービット中心を任意のテーブル点へ移動（隙間の下見用）。aim 追従より優先。 */
  setOrbitTarget: (x: number, y: number, z: number) => void;
  /** オービット中心の上書きを解除し、通常（aim/テーブル中心）追従へ戻す。 */
  clearOrbitTarget: () => void;
  /** ユーザー手動制御フラグをクリアする（自動演出を次フレーム以降で再び有効化）。 */
  clearUserOverride: () => void;
  /** 狙撃カム: ショット時のカメラ構図（位置・方位・仰角・半径・注視点）を即時凍結する。
   *  凍結中は他の自動演出指示（setOverview/setFollow/setAutoTopView 等）を内部で無視し、
   *  最終 camera.position/lookAt も凍結時のスナップショットで固定する（update でアニメ更新を止める）。
   *  解除は releaseFreeze() で行う（即時 or イージング）。
   *  発動条件（手玉・的玉・狙うポケットが画面内）は呼び出し側（main.ts）で判定する。 */
  freezeForShot: () => void;
  /** 狙撃カムの凍結を解除する。easeSec を渡すとその秒数で「凍結スナップショット」から
   *  「通常追従の最終位置」へ滑らかに補間する（既定 0.4 秒）。0 を渡せば即時解除。 */
  releaseFreeze: (easeSec?: number) => void;
  /** 現在ショット凍結中か（解除イージング中は false）。main.ts が「凍結中は setFollow/setAutoTopView を
   *  呼ばない」分岐に使うほか、解除イベントの監視ガードにも使う。 */
  isShotFrozen: () => boolean;
  /** カメラを初期構図へ滑らかに戻す（ビューアングル自動リセット用）。
   *  azimuth を渡せばその方位（手球の後方=エイム方向）へ向け直す。
   *  includeZoom=true なら radius も初期値へ補間（拡大率もリセット）。false（既定）は
   *  radius を維持し、仰角と方位のみ復元する。
   *  ユーザー手動制御フラグもクリアし、自動演出を復活させる。topLocked は維持。
   *  以後のフレームで補間（途中で orbit/zoom 等が入れば中断＝ユーザー操作優先）。 */
  restoreDefault: (azimuth?: number, includeZoom?: boolean) => void;
  /** カメラを初期構図に戻す（リセット時用）。azimuth を渡せばその値で再初期化。 */
  reset: (azimuth?: number) => void;
  /** カメラ半径（テーブル中心からの距離）を更新する。クランプは内部で行う。
   *  画面回転（ポートレート⇄ランドスケープ）でアスペクト比に合わせた距離へ調整するのに使う。
   *  ユーザー手動操作中（userOverride=true）も無視せず適用する（呼び出し側で判定）。 */
  setRadius: (r: number) => void;
  /** 現在のカメラ半径を取得する（ボタンUI等の倍率制御に使用）。 */
  getRadius: () => number;
  /** 現在の方位角（azimuth）を取得する。狙撃カム解放後の方向だけ追従フレーミング
   *  （`shot-freeze-cam.ts:computePostFreezeFraming`）で、2 つの垂直方位候補から
   *  「現在角に近い方」を選んで最小回転を実現するために main.ts が参照する。 */
  getAzimuth: () => number;
  /** カメラ仰角（elevation・ラジアン）をプログラム制御で直接設定する。
   *  Broadcast モード F-31 のローレールプリセット（プリセット #3 Low-Rail Cinematic）等で
   *  既存 `orbit(dEl)` の user clamp（0.08）より低い角度（0.03）を許容するためのプログラム
   *  専用 API。ユーザーの手動 orbit の安全範囲（テーブル下への潜り込み防止）は不変。
   *  範囲外（NaN/Infinity/負値）は無視。userOverride は変更しない（プリセット適用が
   *  ユーザー手動操作扱いにならないようにするため）。 */
  setElevation: (rad: number) => void;
  /** Broadcast モード F-31 プリセット #4 Jib Sweep 用のキーフレーム補間 API。
   *  指定された 2〜N 点の `{ pos, lookAt }` 配列を `durSec` 秒で補間し、カメラ位置と注視点を
   *  滑らかにトラベラーする（既定 easeInOutCubic）。補間中は通常追従の azimuth/radius/elevation
   *  計算をバイパスし、出力のみキーフレーム補間値で固定する（freezeForShot と類似の仕組み）。
   *  補間完了後は自動的に通常追従へ復帰する（手動操作も再度反映可）。
   *  3 点ベジエ（設計書 §9 O-1 確定解）の場合は `pos` 配列を 3 要素（始点・中継点・終点）で渡す。
   *  ユーザー手動操作（`isUserOverridden()`）中は呼び出しを無視する。 */
  panToKeyframes: (
    keyframes: ReadonlyArray<{
      pos: { x: number; y: number; z: number };
      lookAt: { x: number; y: number; z: number };
    }>,
    durSec: number,
  ) => void;
  /** Jib Sweep（panToKeyframes）が補間中か。Director が「補間完了待ち」判定に使う。 */
  isPanning: () => boolean;
  /** Broadcast モード F-31 P2b §12.4 — 滑らかなカット遷移 API。
   *  現在描画している camera.position / lookAt を snapshot として捕捉し、`durSec` 秒で
   *  「snapshot」→「通常追従の現在値」へ補間する。プリセット切替（applyDirectorEffects）の
   *  直前に呼ぶことで、瞬時切替の違和感を解消する。
   *  内部実装は `freezeReleaseBlend` 機構を流用（shotFrozen は立てない＝ワンショット解除のみ）。
   *  panToKeyframes 補間中は smoothCutTo の出力は pan が優先される（pan が先勝ち）。
   *  ユーザー手動操作中（`isUserOverridden()`）は呼び出しを無視する。 */
  smoothCutTo: (durSec: number) => void;
  /** Broadcast モード F-31 P2b §12.6 — カメラ補間時間スケール API。
   *  rig.update(dt) 内の全てのカメラ補間（azimuth / target / blend / freeze release / pan timer）
   *  に対し dt を `scale` 倍する。0.5 = 半速（スロー演出）、1.0 = 通常速度。
   *  **物理 dt（world.step(dt)）には触らない＝決定論的物理を維持**。9 番勝利シーケンサ等で
   *  「カメラだけスロー」を実現する。範囲外（NaN/0/負値）は無視。 */
  setSlowMotion: (scale: number) => void;
  /** Broadcast モード F-31 P3.2 — プリセット切替を **瞬時カット**にするための snap API。
   *  rig 内部の lerp バッファ（azimuth → desiredAzimuth / target → desiredTarget /
   *  cinema/overview/follow/top のブレンド / freezeReleaseBlend / followRadius/Elev）を
   *  全て desired/target 値へ **即時収束** させる。次フレームの update() では lerp 残量
   *  ゼロのため、視覚上「別カメラに切り替わった」ように見える。
   *  `applyDirectorEffects` が preset.apply の直後に呼ぶ。§13 F-2「ライブスポーツは
   *  カットのみ・フェード禁止」に整合（旧 smoothCutTo(0.4) は §14 初版の誤実装）。 */
  snapInternalState: () => void;
  /** Broadcast モード F-31 P3 §14.5 — topBlend 補間中のアスペクト依存 azimuth 自動回転の抑止。
   *  topLocked / autoTop でトップビューへ遷移する際、内部では camera.aspect から `topFitAz`
   *  を自動選択し、azimuth を「テーブル長辺が画面長辺に揃う向き」へ補間する。これは旧 Hot Head
   *  プリセットの利便のためだが、Broadcast Master（短クッション側 20–25° の主役）では
   *  topBlend を使わず通常追従するため本機能は不要。本 API はその「自動回転」を **抑止**
   *  するためのフラグで、`true` のときは topFitAz の選択を行わず azimuth は現状値を維持する。
   *  **新 BM プリセットでは topBlend を立てないため実害ゼロ**だが、§13 F-6 の旋回バグ
   *  （Hot Head 廃止前のユーザー指摘）に対する保険として残す。 */
  setBroadcastAzimuthLock: (locked: boolean) => void;
  /** カメラ半径を倍率で変更する（factor<1で拡大寄り・>1で縮小寄り）。
   *  内部で min/max にクランプ。ユーザー手動操作扱い（userOverride=true）にする。
   *  ⊕/⊖ ボタン等の明示的なユーザーズーム入力に使う。 */
  zoomBy: (factor: number) => void;
  /** 初期半径（reset 時に戻る基準値）を更新する。setRadius と併用してリセット動作も最新化する。 */
  setInitialRadius: (r: number) => void;
  /** ユーザーが手動 orbit/zoom した後か（resize で自動調整するか判定する用）。 */
  isUserOverridden: () => boolean;
  update: (dt: number) => void;
}

export interface RigOptions {
  readonly azimuth?: number;
  readonly elevation?: number;
  readonly radius?: number;
  readonly minRadius?: number;
  readonly maxRadius?: number;
}

const lerpAngle = (a: number, b: number, t: number): number => {
  let d = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

export const createCameraRig = (
  camera: THREE.PerspectiveCamera,
  opts: RigOptions = {},
): CameraRig => {
  let azimuth = opts.azimuth ?? -Math.PI / 2;
  let elevation = opts.elevation ?? 0.5;
  let radius = opts.radius ?? 2.0;
  const minRadius = opts.minRadius ?? 0.6;
  const maxRadius = opts.maxRadius ?? 6;

  const target = new THREE.Vector3(0, 0, 0);
  const desiredTarget = new THREE.Vector3(0, 0, 0);
  const aimTarget = new THREE.Vector3(0, 0, 0);
  // オービット中心の上書き（下見用）。null なら通常（aim/テーブル中心）追従。
  let orbitOverride: THREE.Vector3 | null = null;

  let aligning = false;
  let desiredAzimuth = azimuth;
  let aiming = false;
  // シネマモード（再生中の引き）: radius にこのスケールを掛けて描画半径とする。
  let cinemaScale = 1;
  let targetCinemaScale = 1;
  const CINEMA_PULL = 1.5;
  // ショット俯瞰モード（発射後にテーブル全体を見せる）。on/off を 0..1 で滑らかに補間。
  // azimuth は現在のエイム方位を保つため触らない（向きの連続性）。
  let overviewBlend = 0;
  let targetOverview = 0;
  const OVERVIEW_RADIUS = 4.8; // テーブル全体が収まる距離（≈52°・縦長aspect余裕込み）
  const OVERVIEW_ELEV = 0.92; // ≈ 52°（トップダウン過ぎず的玉とポケットが見える角度）
  // ダイナミック・フォローフレーミング（ショット後の追跡構図）。
  // 設計方針:
  //  - 方位(azimuth)は固定（ショット方向の真後ろ）＝球を追って回さない＝アングル切替なし。
  //  - 注視点は「動いている球＋手球・的球の境界箱の中心」へ**緩やかに**追従（重心ジャンプを排除）。
  //  - 距離・仰角は球の散らばり(spread)から決め、**非対称スムージング**で安定化
  //    （広がる時は速く引き／高く＝球を逃さない、縮む時は遅く寄り／低く＝ポンピング防止）。
  //  - 散らばりが大きいほど仰角を上げて俯瞰寄りにする（ブレイク等は自然にトップ寄りへ）。
  let followBlend = 0;
  let targetFollow = 0;
  // setFollowFrame が毎フレーム算出する「生」のターゲット。
  const rawFollowCenter = new THREE.Vector2(0, 0);
  let rawFollowRadius = OVERVIEW_RADIUS;
  let rawFollowElev = 0.5;
  // update が滑らかに追従させる確定値（実際にカメラへ反映）。
  const followCenter = new THREE.Vector2(0, 0);
  let followRadius = OVERVIEW_RADIUS;
  let followElev = 0.5;
  let followInit = false; // 突入直後に確定値を raw へスナップ初期化したか
  const FOLLOW_MARGIN = 1.25; // 収める集合の余白
  const FOLLOW_MIN_RADIUS = 1.5; // 寄りすぎ防止
  const FOLLOW_MAX_RADIUS = OVERVIEW_RADIUS; // 引きすぎ上限（≈テーブル全体）
  const FOLLOW_ELEV_LOW = 0.42; // 球が密集＝低い迫力アングル（≈24°）
  const FOLLOW_ELEV_HIGH = 1.25; // 広く散らばる＝ほぼ俯瞰（≈72°）
  const FOLLOW_SPREAD_LOW = 0.35; // 中心からの最大距離[m] がこれ以下なら低アングル
  const FOLLOW_SPREAD_HIGH = 1.25; // これ以上なら高アングル（線形補間）
  // 真上トップビュー。手動ロック（topLocked・持続）と自動（autoTop・ユーザー操作で解除）。
  let topLocked = false;
  // topLocked の発生源。'manual'=ユーザー明示／'auto'=システム由来（「対象球が隠れたら自動トップ」等）。
  // HUDの自動半透明スキップ判定（isAutoTopActive）で使う。`topLocked=false` のときの値は意味を持たない。
  let topLockedSource: 'manual' | 'auto' = 'manual';
  let autoTop = false;
  let topBlend = 0;
  // トップビューの確定向き（突入時に1回決め、トップビュー中は固定して180°反転を防ぐ）。
  let topFitAz: number | null = null;
  const TOP_ELEV = Math.PI / 2 - 0.03; // ≈ 88.3°（gimbal 回避のため厳密 90° は避ける）
  // トップビューのフィット用テーブル外周半幅（常に画面フィット）。
  let topFitHX = 1.4;
  let topFitHY = 0.8;
  const TOP_FIT_MARGIN = 1.06;

  // トップビューのピンチズーム（1=全体フィット）＋パン（注視点オフセット・ワールドxy）。
  const MAX_TOP_ZOOM = 4;
  let topZoom = 1;
  const topPanV = new THREE.Vector2(0, 0);
  // update が毎フレーム記録する「トップビューの確定向き」と「ズーム非依存の基準半径」。
  // ピンチ/パンのワールド換算・パンのクランプに使う。
  let lastRenderAz = 0;
  let lastTopUnitRadius = 1;
  const _ndcRay = new THREE.Vector3();

  // 真上から見てテーブル外周が画面（縦長含む）に収まる距離を算出。
  // 指定 azimuth でテーブルの画面x/y方向への張り出しを求め、縦横の視野に合わせる。
  const topFitRadius = (az: number): number => {
    const halfV = Math.tan(((camera.fov * Math.PI) / 180) / 2); // 単位距離あたりの縦半幅
    const aspect = camera.aspect; // 横/縦
    const cs = Math.abs(Math.cos(az));
    const sn = Math.abs(Math.sin(az));
    // 画面右方向=(-sinθ,cosθ), 画面上方向=(-cosθ,-sinθ) への投影張り出し。
    const rx = topFitHX * sn + topFitHY * cs; // 画面横方向の半幅（ワールド）
    const ry = topFitHX * cs + topFitHY * sn; // 画面縦方向の半幅（ワールド）
    const rNeed = Math.max(rx / (halfV * aspect), ry / halfV);
    return rNeed * TOP_FIT_MARGIN;
  };

  // 候補角のうち az に最も近いものを返す（180°/反対も同じ向きとして扱える）。
  const nearestAngle = (az: number, candidates: number[]): number => {
    let best = candidates[0]!;
    let bestGap = Infinity;
    for (const c of candidates) {
      let d = ((c - az + Math.PI) % (Math.PI * 2)) - Math.PI;
      if (d < -Math.PI) d += Math.PI * 2;
      const gap = Math.abs(d);
      if (gap < bestGap) {
        bestGap = gap;
        best = c;
      }
    }
    return best;
  };
  // 狙撃カム（shot freeze）: ショット直前のカメラ位置/注視点を保存し、ポケットイン or 解除指示まで
  // 完全固定する。`shotFrozen=true` の間は他の自動演出（cinema/overview/follow/topView）の
  // 入力を内部で無視し、update() の最終出力（camera.position/lookAt）も snapshot 値で固定する。
  // 解除時は freezeReleaseBlend を 1→0 へ補間して「snapshot」から「通常追従の現在値」へ復帰する。
  let shotFrozen = false;
  const freezeCamPos = new THREE.Vector3();
  const freezeTarget = new THREE.Vector3();
  // 解除イージング: 1=完全凍結スナップショット, 0=通常追従。releaseFreeze() で 1 から
  // 設定秒数で 0 へ線形減衰させる。0 になったら解除完了。
  let freezeReleaseBlend = 0;
  let freezeReleaseDuration = 0.4; // 秒

  // ユーザーが手動で orbit/zoom 操作したら true。これが立っている間は
  // setCinema(true)/setOverview(true) を無視し、手動操作を優先する。
  let userOverride = false;
  // ビューアングル自動リセット中フラグ（仰角・半径を個別に管理）。
  //   - restoringElev: 仰角を初期値へ補間中
  //   - restoringRadius: 半径（ズーム）を初期値へ補間中
  // 設定「ズームも含む」OFF のときは restoringRadius=false で半径維持。
  // orbit/zoom/zoomBy 等のユーザー操作で即座に両フラグ中断（手動優先）。reset() でもクリア。
  let restoringElev = false;
  let restoringRadius = false;
  // 自動リセット補間のスナップ閾値（半径=メートル / 仰角=ラジアン・約 0.57°）。
  // この差以内になったら強制スナップして該当フラグを false に落とす。
  const RESTORE_RADIUS_EPS = 0.001;
  const RESTORE_ELEV_EPS = 0.01;
  // 初期値を保持しておく（reset 用）。アスペクト比変化時に setInitialRadius で更新可能。
  let initialRadius = radius;
  const initialElevation = elevation;
  const setRadius: CameraRig['setRadius'] = (r) => {
    radius = clamp(r, minRadius, maxRadius);
  };
  const setInitialRadius: CameraRig['setInitialRadius'] = (r) => {
    initialRadius = clamp(r, minRadius, maxRadius);
  };
  const getRadius: CameraRig['getRadius'] = () => radius;
  const getAzimuth: CameraRig['getAzimuth'] = () => azimuth;
  // Broadcast モード F-31 用のプログラム専用 min elevation（ローレールシネマ）。
  // ユーザー orbit の clamp は 0.08 のまま維持し、プログラム制御のみ 0.03 を許容する
  // （`mobile-hud-ux-guidelines.md` 由来の「ユーザーがカメラをテーブル下に潜らせない」原則の遵守）。
  const MIN_ELEV_PROGRAM = 0.03;
  const MAX_ELEV_PROGRAM = 1.45;
  const setElevation: CameraRig['setElevation'] = (rad) => {
    if (!Number.isFinite(rad)) return;
    elevation = clamp(rad, MIN_ELEV_PROGRAM, MAX_ELEV_PROGRAM);
    // プリセット適用はユーザー手動操作扱いにしない（userOverride 不変）。
    // 自動リセット復元中なら止める（プログラムが意図的に新仰角を設定したため）。
    restoringElev = false;
  };

  // ---- panToKeyframes（Jib Sweep）---- F-31 P2 プリセット #4 用。
  // 補間中は updateForOutput() の末尾で「キーフレーム補間値」を採用し、通常追従計算を上書きする
  // （shotFrozen と同じく出力差し替え方式）。完了後は panning=false へ落として通常追従へ復帰。
  interface PanKeyframe {
    pos: { x: number; y: number; z: number };
    lookAt: { x: number; y: number; z: number };
  }
  let panKfs: ReadonlyArray<PanKeyframe> | null = null;
  let panDuration = 0; // 秒
  let panElapsed = 0; // 秒
  const panToKeyframes: CameraRig['panToKeyframes'] = (keyframes, durSec) => {
    if (userOverride) return;
    if (!keyframes || keyframes.length < 2) return;
    if (!Number.isFinite(durSec) || durSec <= 0) return;
    panKfs = keyframes;
    panDuration = durSec;
    panElapsed = 0;
    // 同時に動いている他自動演出を抑止（出力上書きされるため整合性のために off へ）。
    targetOverview = 0;
    targetFollow = 0;
    targetCinemaScale = 1;
    autoTop = false;
  };
  const isPanning: CameraRig['isPanning'] = () =>
    panKfs !== null && panElapsed < panDuration;

  // F-31 P2b §12.4 smoothCutTo: 現在描画位置/注視点を snapshot として、`durSec` 秒で
  // 通常追従値へ補間する。`freezeReleaseBlend` 機構を流用するが `shotFrozen` は立てない
  // （ワンショット解除のみ）。panToKeyframes 中は pan の出力が優先されるため smoothCutTo は
  // 静かに無効化される（panKfs!==null で early-return）。
  const smoothCutTo: CameraRig['smoothCutTo'] = (durSec) => {
    if (userOverride) return; // 手動操作優先
    if (panKfs !== null) return; // pan 中は pan が出力を握る
    if (!Number.isFinite(durSec) || durSec <= 0) return;
    freezeCamPos.copy(lastRenderPos);
    freezeTarget.copy(lastRenderTarget);
    freezeReleaseBlend = 1; // 100% snapshot → 0% snapshot へ減衰
    freezeReleaseDuration = durSec;
    // shotFrozen は立てない（snapshot を 1 フレームだけ採用＝即解除モードでもなく即時遷移でもない）
  };

  // F-31 P2b §12.6 setSlowMotion: rig.update(dt) 内の補間 dt をスケール。
  // 物理 dt（world.step）には影響しない＝決定論維持。9 番勝利シーケンサ等で使う。
  let cameraTimeScale = 1;
  const setSlowMotion: CameraRig['setSlowMotion'] = (scale) => {
    if (!Number.isFinite(scale) || scale <= 0) return;
    cameraTimeScale = scale;
  };

  // F-31 P3 §14.5: Broadcast モード時の topBlend azimuth 自動回転抑止フラグ（保険）。
  // true のとき、`update()` 内の `topFitAz` 自動選択をバイパスし azimuth を維持する。
  let broadcastAzimuthLocked = false;
  const setBroadcastAzimuthLock: CameraRig['setBroadcastAzimuthLock'] = (
    locked,
  ) => {
    broadcastAzimuthLocked = !!locked;
  };

  // F-31 P3.2 §14.2.4: プリセット切替の瞬時カット化 snap API。
  // applyDirectorEffects が preset.apply の直後に呼び、rig 内部の lerp バッファを
  // 全て desired/target 値へ即時収束させる。視覚上 1 フレームで切替＝「別カメラ」感を実現。
  // P3.3 修正: `desiredTarget` は update() 内で都度計算される（orbitOverride/aiming/aimTarget
  // から）ため、snap の冒頭で **最新値を再計算してから target を copy する**。これをしないと
  // target は前プリセット時の古い desiredTarget へ snap され、次フレームの update() で
  // 新プリセットの desiredTarget へ lerp し直す＝視覚上の移動アニメが残る。
  const snapInternalState: CameraRig['snapInternalState'] = () => {
    // 1) 最新の desiredTarget を update() のロジックと同じ規則で再計算する
    //    （優先順位: orbitOverride > aiming(aimTarget) > テーブル中心）
    if (orbitOverride) desiredTarget.copy(orbitOverride);
    else if (aiming) desiredTarget.copy(aimTarget);
    else desiredTarget.set(0, 0, 0);
    // 2) target（lookAt 注視点）→ 再計算済 desiredTarget へ即時収束
    target.copy(desiredTarget);
    // 3) azimuth → desiredAzimuth へ即時収束（alignToAim で desiredAzimuth が更新済の前提）
    if (aligning) azimuth = desiredAzimuth;
    // 4) 各種ブレンドを target 値へ即時収束
    cinemaScale = targetCinemaScale;
    overviewBlend = targetOverview;
    followBlend = targetFollow;
    // フォローフレーミングの確定値も raw 値へ snap
    followCenter.copy(rawFollowCenter);
    followRadius = rawFollowRadius;
    followElev = rawFollowElev;
    followInit = true; // 次回 setFollowFrame 時のスナップ初期化フラグを立てておく
    // top blend は topLocked/autoTop に応じて 0/1 へ即時切替
    topBlend = topLocked || autoTop ? 1 : 0;
    // freezeReleaseBlend（旧 smoothCutTo の残量・shotFreezeCam 解除）もクリア
    freezeReleaseBlend = 0;
    // 自動リセット復元中なら snap で目標到達済とみなしてクリア（途中状態が残らない）
    restoringElev = false;
    restoringRadius = false;
  };
  const zoomBy: CameraRig['zoomBy'] = (factor) => {
    if (!Number.isFinite(factor) || factor <= 0) return;
    radius = clamp(radius * factor, minRadius, maxRadius);
    // ボタンUIによる明示的な操作 → 自動演出（cinema/overview/自動トップ）を抑止。
    // ショット後に main.ts が rig.clearUserOverride() でリセットする既存実装と整合。
    userOverride = true;
    // ユーザー操作で自動リセット復元を中断（仰角・半径とも）。
    restoringElev = false;
    restoringRadius = false;
  };
  const isUserOverridden: CameraRig['isUserOverridden'] = () => userOverride;

  camera.up.set(0, 0, 1);

  const orbit: CameraRig['orbit'] = (dAz, dEl) => {
    aligning = false; // 手動操作中はエイム追従を解除
    azimuth += dAz;
    elevation = clamp(elevation + dEl, 0.08, 1.45);
    // 手動操作: 自動演出（引き/俯瞰/自動トップ）を解除し、それ以降のフレームでも無効化。
    // 手動ロックのトップビュー（topLocked）は解除しない（再押下まで持続）。
    userOverride = true;
    // ユーザー操作で自動リセット復元を中断（仰角・半径とも）。
    restoringElev = false;
    restoringRadius = false;
    targetCinemaScale = 1;
    targetOverview = 0;
    targetFollow = 0;
    autoTop = false;
  };

  const zoom: CameraRig['zoom'] = (factor) => {
    radius = clamp(radius * factor, minRadius, maxRadius);
    userOverride = true;
    // ユーザー操作で自動リセット復元を中断（仰角・半径とも）。
    restoringElev = false;
    restoringRadius = false;
    targetCinemaScale = 1;
    targetOverview = 0;
    targetFollow = 0;
    autoTop = false;
  };

  // 指の NDC（[-1,1]）からテーブル平面 z=0 上のワールド座標を求める（現在のカメラで逆投影）。
  const planeFromNdc = (nx: number, ny: number): THREE.Vector2 => {
    camera.updateMatrixWorld();
    _ndcRay.set(nx, ny, 0.5).unproject(camera).sub(camera.position);
    const t = _ndcRay.z !== 0 ? -camera.position.z / _ndcRay.z : 0;
    return new THREE.Vector2(
      camera.position.x + _ndcRay.x * t,
      camera.position.y + _ndcRay.y * t,
    );
  };

  // パンをテーブル範囲内（隅の球まで見える範囲）にクランプ。renderAz はテーブル軸を
  // 画面軸に揃えるため、画面の縦横半幅をワールドx/y軸へ割り当ててクランプする。
  const clampTopPan = (): void => {
    const R = lastTopUnitRadius / topZoom;
    const halfV = Math.tan(((camera.fov * Math.PI) / 180) / 2);
    const vH = R * halfV; // 画面縦の半幅（ワールド）
    const hH = vH * camera.aspect; // 画面横の半幅（ワールド）
    const alongX = Math.abs(Math.cos(lastRenderAz)) > 0.5; // テーブル長辺(x)が画面縦か
    const viewHalfX = alongX ? vH : hH;
    const viewHalfY = alongX ? hH : vH;
    const cx = Math.max(0, topFitHX - viewHalfX);
    const cy = Math.max(0, topFitHY - viewHalfY);
    topPanV.x = clamp(topPanV.x, -cx, cx);
    topPanV.y = clamp(topPanV.y, -cy, cy);
  };

  const topPinch: CameraRig['topPinch'] = (factor, nx, ny) => {
    if (topBlend < 0.5 || !Number.isFinite(factor) || factor <= 0) return;
    const before = planeFromNdc(nx, ny); // ズーム中心のワールド点
    const oldZoom = topZoom;
    topZoom = clamp(topZoom / factor, 1, MAX_TOP_ZOOM);
    const k = oldZoom / topZoom; // = R_new / R_old
    // before を画面上の同じ位置に保つよう注視点を移動。
    topPanV.x = before.x - (before.x - topPanV.x) * k;
    topPanV.y = before.y - (before.y - topPanV.y) * k;
    clampTopPan();
  };

  const topPan: CameraRig['topPan'] = (pnx, pny, nnx, nny) => {
    if (topBlend < 0.5 || topZoom <= 1.0001) return;
    const wPrev = planeFromNdc(pnx, pny);
    const wNow = planeFromNdc(nnx, nny);
    topPanV.x -= wNow.x - wPrev.x; // 指の下の点が追従するよう逆方向へ
    topPanV.y -= wNow.y - wPrev.y;
    clampTopPan();
  };

  const resetTopZoom: CameraRig['resetTopZoom'] = () => {
    topZoom = 1;
    topPanV.set(0, 0);
  };
  const isTopView: CameraRig['isTopView'] = () => topBlend > 0.5;
  const getTopZoom: CameraRig['getTopZoom'] = () => topZoom;

  const alignToAim: CameraRig['alignToAim'] = (angle) => {
    aligning = true;
    desiredAzimuth = angle + Math.PI; // 手球の後方からショット方向を見る
  };

  const setAimTarget: CameraRig['setAimTarget'] = (x, y, z) => {
    aimTarget.set(x, y, z);
  };

  const setFraming: CameraRig['setFraming'] = (a) => {
    aiming = a;
  };

  const setCinema: CameraRig['setCinema'] = (active) => {
    if (active && userOverride) return; // 手動操作中は自動演出を無視
    if (active && shotFrozen) return; // 狙撃カム凍結中は自動演出を抑止
    targetCinemaScale = active ? CINEMA_PULL : 1;
  };

  const setOverview: CameraRig['setOverview'] = (active) => {
    if (active && userOverride) return;
    if (active && shotFrozen) return; // 狙撃カム凍結中は自動演出を抑止
    targetOverview = active ? 1 : 0;
  };

  const setFollow: CameraRig['setFollow'] = (active) => {
    if (active && userOverride) return; // 手動操作中は自動演出を無視
    if (active && shotFrozen) return; // 狙撃カム凍結中は自動演出を抑止
    if (active && targetFollow === 0) followInit = false; // 突入時に確定値を raw へスナップ
    targetFollow = active ? 1 : 0;
  };

  const setFollowFrame: CameraRig['setFollowFrame'] = (primary, secondary) => {
    if (primary.length === 0) return; // 対象なし: 直近の構図を保持
    // 全アクティブ球（primary∪secondary）の境界箱中心を構図の中心にする
    // （重心と違い遠い1球に引きずられにくく、注視点が安定する）。
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const expand = (p: { x: number; y: number }): void => {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    };
    for (const p of primary) expand(p);
    for (const p of secondary) expand(p);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    rawFollowCenter.set(cx, cy);

    // 中心からの最大距離（primary=必ず収める / all=余裕があれば収める）。
    let rPrimary = 0;
    for (const p of primary) {
      const d = Math.hypot(p.x - cx, p.y - cy);
      if (d > rPrimary) rPrimary = d;
    }
    let rAll = rPrimary;
    for (const p of secondary) {
      const d = Math.hypot(p.x - cx, p.y - cy);
      if (d > rAll) rAll = d;
    }
    // 画面の狭い方の半画角でフィット（縦横どちらでも確実に収まる保守的な円フィット）。
    const halfV = Math.tan(((camera.fov * Math.PI) / 180) / 2);
    const halfMin = Math.max(Math.min(halfV, halfV * camera.aspect), 1e-3);
    const needPrimary = (rPrimary * FOLLOW_MARGIN) / halfMin;
    const needAll = (rAll * FOLLOW_MARGIN) / halfMin;
    // secondary はキャップ内で best-effort。primary は必ず収める（キャップ超過も許容）。
    let r = Math.min(needAll, FOLLOW_MAX_RADIUS);
    if (r < needPrimary) r = needPrimary;
    rawFollowRadius = clamp(r, FOLLOW_MIN_RADIUS, maxRadius);

    // 散らばり（中心からの最大距離）に応じて仰角を上げる（広い=俯瞰寄り）。
    const t = clamp(
      (rAll - FOLLOW_SPREAD_LOW) / (FOLLOW_SPREAD_HIGH - FOLLOW_SPREAD_LOW),
      0,
      1,
    );
    rawFollowElev = FOLLOW_ELEV_LOW + (FOLLOW_ELEV_HIGH - FOLLOW_ELEV_LOW) * t;

    // 突入直後は確定値を生値へスナップ（前ショットの残値からの不自然な補間を防ぐ）。
    if (!followInit) {
      followCenter.copy(rawFollowCenter);
      followRadius = rawFollowRadius;
      followElev = rawFollowElev;
      followInit = true;
    }
  };

  const setTopViewLocked: CameraRig['setTopViewLocked'] = (active, source) => {
    topLocked = active; // 持続。ユーザー操作では解除されない。
    // active=true の時のみ source を更新。false の時は source は意味を持たないので保持しない（既定 'manual' に戻す）。
    // source 省略は手動扱い（既存挙動の互換）。
    topLockedSource = active ? (source ?? 'manual') : 'manual';
  };

  const setAutoTopView: CameraRig['setAutoTopView'] = (active) => {
    if (active && userOverride) return; // 手動操作中は無視
    if (active && shotFrozen) return; // 狙撃カム凍結中は自動トップビューを抑止
    autoTop = active;
  };

  // ---- 狙撃カム（shot freeze）API ----
  // 凍結時の最終 camera.position/lookAt を覚えるため update() 末尾の値を別変数へ写しておく。
  const lastRenderPos = new THREE.Vector3();
  const lastRenderTarget = new THREE.Vector3();
  const freezeForShot: CameraRig['freezeForShot'] = () => {
    // 直近の描画位置・注視点を凍結対象として固定する。発射時点の構図を維持する。
    freezeCamPos.copy(lastRenderPos);
    freezeTarget.copy(lastRenderTarget);
    shotFrozen = true;
    freezeReleaseBlend = 1; // 完全凍結
    // 他の自動演出が「直前の値」のまま自走しないよう、ターゲットを現状値に揃える
    // （解除時に通常追従へ戻る際の挙動を予測可能にする）。
    targetOverview = 0;
    targetFollow = 0;
    targetCinemaScale = 1;
    autoTop = false;
  };
  const releaseFreeze: CameraRig['releaseFreeze'] = (easeSec) => {
    if (!shotFrozen && freezeReleaseBlend <= 0) return; // 既に通常追従中
    shotFrozen = false;
    freezeReleaseDuration = Math.max(0, easeSec ?? 0.4);
    if (freezeReleaseDuration === 0) freezeReleaseBlend = 0; // 即時解除
    // freezeReleaseBlend は update() で時間減衰させ、snapshot ↔ 通常追従を補間する
  };
  const isShotFrozen: CameraRig['isShotFrozen'] = () => shotFrozen;

  const isAutoTopActive: CameraRig['isAutoTopActive'] = () => {
    // ショット後自動（autoTop）と「対象球が隠れたら自動トップ」（topLocked && source==='auto'）のいずれかで true。
    // 手動ロック中（topLocked && source==='manual'）はユーザー明示なので false（既存の半透明挙動を維持）。
    return autoTop || (topLocked && topLockedSource === 'auto');
  };

  const setTopFitExtents: CameraRig['setTopFitExtents'] = (hx, hy) => {
    topFitHX = hx;
    topFitHY = hy;
  };

  const clearUserOverride: CameraRig['clearUserOverride'] = () => {
    userOverride = false;
  };

  const restoreDefault: CameraRig['restoreDefault'] = (newAzimuth, includeZoom) => {
    // 仰角は常に復元対象（自動リセットの主目的）。半径は includeZoom=true のときのみ。
    restoringElev = true;
    restoringRadius = includeZoom === true;
    if (newAzimuth !== undefined && Number.isFinite(newAzimuth)) {
      aligning = true;
      desiredAzimuth = newAzimuth;
    }
    // 自動演出（オートトップ/引き/俯瞰/フォロー）を復活させるため userOverride を解除。
    // 注意: topLocked（手動トップビュー）はユーザーの明示状態なので維持する。
    userOverride = false;
  };

  const setOrbitTarget: CameraRig['setOrbitTarget'] = (x, y, z) => {
    if (!orbitOverride) orbitOverride = new THREE.Vector3();
    orbitOverride.set(x, y, z);
    aligning = false; // 下見中は手球後方への自動方位追従を止め、手動オービットに委ねる
  };
  const clearOrbitTarget: CameraRig['clearOrbitTarget'] = () => {
    orbitOverride = null;
  };

  const reset: CameraRig['reset'] = (newAzimuth) => {
    radius = initialRadius;
    elevation = initialElevation;
    if (newAzimuth !== undefined) {
      azimuth = newAzimuth;
      desiredAzimuth = newAzimuth;
    }
    targetCinemaScale = 1;
    cinemaScale = 1;
    targetOverview = 0;
    overviewBlend = 0;
    targetFollow = 0;
    followBlend = 0;
    followInit = false;
    followCenter.set(0, 0);
    rawFollowCenter.set(0, 0);
    followRadius = OVERVIEW_RADIUS;
    rawFollowRadius = OVERVIEW_RADIUS;
    followElev = FOLLOW_ELEV_LOW;
    rawFollowElev = FOLLOW_ELEV_LOW;
    topLocked = false;
    autoTop = false;
    topBlend = 0;
    topZoom = 1;
    topPanV.set(0, 0);
    userOverride = false;
    // 完全リセット時は自動復元中フラグもクリア。
    restoringElev = false;
    restoringRadius = false;
    aligning = false;
    orbitOverride = null;
    // 狙撃カム凍結も完全解除（再ラック/モード変更時の意図しないフリーズ残留を防ぐ）。
    shotFrozen = false;
    freezeReleaseBlend = 0;
    // F-31 P2b §12.6: スローモーションも通常速度へ戻す（リセット時に演出残留を防ぐ）。
    cameraTimeScale = 1;
    // panToKeyframes 進行中の補間も中断。
    panKfs = null;
  };

  const update: CameraRig['update'] = (dtRaw) => {
    // F-31 P2b §12.6: setSlowMotion(scale) で全カメラ補間の dt をスケールする。
    // 以降の `dt` 参照は全てスケール後の値（rig.update に渡された生 dt は `dtRaw`）。
    // 物理 dt（world.step）には影響しない＝決定論維持（呼び出し側 main.ts の制御）。
    const dt = dtRaw * cameraTimeScale;
    if (aligning) azimuth = lerpAngle(azimuth, desiredAzimuth, 1 - Math.pow(0.001, dt));
    // ビューアングル自動リセット中: 仰角・半径を個別フラグで初期値へ滑らかに補間。
    // 補間係数は他の自動演出（cinema/overview/follow=0.05）に揃え、約 0.4 秒で収束する
    // 体感とする。十分近づいたらスナップして該当フラグを false に落とす。
    if (restoringElev || restoringRadius) {
      const c = 1 - Math.pow(0.05, dt);
      if (restoringElev) {
        elevation += (initialElevation - elevation) * c;
        if (Math.abs(elevation - initialElevation) < RESTORE_ELEV_EPS) {
          elevation = initialElevation;
          restoringElev = false;
        }
      }
      if (restoringRadius) {
        radius += (initialRadius - radius) * c;
        if (Math.abs(radius - initialRadius) < RESTORE_RADIUS_EPS) {
          radius = initialRadius;
          restoringRadius = false;
        }
      }
    }
    // 下見の上書きがあればそれを優先。次に狙い中=手球、他=テーブル中心。
    if (orbitOverride) desiredTarget.copy(orbitOverride);
    else if (aiming) desiredTarget.copy(aimTarget);
    else desiredTarget.set(0, 0, 0);
    target.lerp(desiredTarget, 1 - Math.pow(0.02, dt));

    // シネマスケールを滑らかに追従させる。
    cinemaScale +=
      (targetCinemaScale - cinemaScale) * (1 - Math.pow(0.05, dt));
    // 俯瞰ブレンドを滑らかに追従させる。
    overviewBlend +=
      (targetOverview - overviewBlend) * (1 - Math.pow(0.05, dt));
    // フォローブレンドを滑らかに追従させる。
    followBlend += (targetFollow - followBlend) * (1 - Math.pow(0.05, dt));
    // フォロー確定値を生値へ追従させる。中心は緩やかに（注視点の安定）。
    // 距離・仰角は非対称: 広がる(引き/上げ)は速く＝球を逃さない、縮む(寄り/下げ)は遅く＝ポンピング防止。
    const cCenter = 1 - Math.pow(0.15, dt);
    followCenter.x += (rawFollowCenter.x - followCenter.x) * cCenter;
    followCenter.y += (rawFollowCenter.y - followCenter.y) * cCenter;
    const baseR = rawFollowRadius > followRadius ? 0.03 : 0.5;
    followRadius += (rawFollowRadius - followRadius) * (1 - Math.pow(baseR, dt));
    const baseE = rawFollowElev > followElev ? 0.06 : 0.5;
    followElev += (rawFollowElev - followElev) * (1 - Math.pow(baseE, dt));
    // トップビューブレンド（手動ロック or 自動）。
    const targetTop = topLocked || autoTop ? 1 : 0;
    topBlend += (targetTop - topBlend) * (1 - Math.pow(0.05, dt));
    // トップビューを抜けたらズーム/パンを既定へ戻す（次回突入は全体フィットから）。
    if (targetTop === 0 && (topZoom !== 1 || topPanV.x !== 0 || topPanV.y !== 0)) {
      topZoom = 1;
      topPanV.set(0, 0);
    }

    // 通常モードのカメラパラメータ
    const normalRadius = clamp(
      radius * cinemaScale,
      minRadius,
      maxRadius * CINEMA_PULL,
    );
    const normalElev = elevation;
    // 俯瞰モードはテーブル中心固定。ブレンド比でターゲットを 0,0,0 寄りに引く。
    const ovRadius =
      normalRadius * (1 - overviewBlend) + OVERVIEW_RADIUS * overviewBlend;
    const ovElev =
      normalElev * (1 - overviewBlend) + OVERVIEW_ELEV * overviewBlend;
    const ovTargetX = target.x * (1 - overviewBlend);
    const ovTargetY = target.y * (1 - overviewBlend);
    const ovTargetZ = target.z * (1 - overviewBlend);

    // フォローフレーミング: 動的な中心・距離・低めの仰角を俯瞰の上へブレンドする。
    // azimuth（=エイム方位）は維持し、ショット方向が画面奥に来る向きのまま球を追う。
    const flRadius = ovRadius * (1 - followBlend) + followRadius * followBlend;
    const flElev = ovElev * (1 - followBlend) + followElev * followBlend;
    const flTargetX = ovTargetX * (1 - followBlend) + followCenter.x * followBlend;
    const flTargetY = ovTargetY * (1 - followBlend) + followCenter.y * followBlend;
    const flTargetZ = ovTargetZ * (1 - followBlend);

    // トップビューでは、テーブル長辺を画面長辺に合わせる方位へ補正
    //（縦画面=縦/横画面=横）して画面を最大活用する。topBlend でなめらかに遷移。
    // P3 §14.5: broadcastAzimuthLocked のとき、azimuth 自動補正をスキップして現状を維持。
    let renderAz = azimuth;
    if (topBlend > 0.001 && !broadcastAzimuthLocked) {
      const candidates =
        camera.aspect < 1 ? [0, Math.PI] : [Math.PI / 2, -Math.PI / 2];
      // 突入時に向きを確定し、トップビュー中は固定（途中で 180° 反転させない）。
      // アスペクト変化（端末回転）で候補集合が変わった時のみ選び直す。
      if (topFitAz === null || !candidates.includes(topFitAz)) {
        topFitAz = nearestAngle(azimuth, candidates);
      }
      renderAz = lerpAngle(azimuth, topFitAz, topBlend);
    } else {
      topFitAz = null; // 非トップビューへ戻ったら確定向きをリセット
    }

    // トップビューを上書きブレンド（俯瞰/通常 → 真上・テーブル中心）。
    // ズーム非依存の基準半径と確定向きを記録（ピンチ/パンのワールド換算に使う）。
    lastRenderAz = renderAz;
    lastTopUnitRadius = topFitRadius(renderAz);
    const topRadius = lastTopUnitRadius / topZoom; // ピンチで距離を縮めて拡大
    const effectiveRadius = flRadius * (1 - topBlend) + topRadius * topBlend;
    const effectiveElev = flElev * (1 - topBlend) + TOP_ELEV * topBlend;
    // トップビューでは注視点を topPan へ寄せる（パン）。非トップビューは従来どおり。
    const fx = flTargetX * (1 - topBlend) + topPanV.x * topBlend;
    const fy = flTargetY * (1 - topBlend) + topPanV.y * topBlend;
    const fz = flTargetZ * (1 - topBlend);

    const ce = Math.cos(effectiveElev);
    const se = Math.sin(effectiveElev);
    // 通常追従が算出した「最終 position / lookAt target」
    const normalX = fx + effectiveRadius * ce * Math.cos(renderAz);
    const normalY = fy + effectiveRadius * ce * Math.sin(renderAz);
    const normalZ = fz + effectiveRadius * se;

    // Jib Sweep（panToKeyframes）補間中は通常追従の最終出力をキーフレーム補間値で上書き。
    // 補間完了後は panKfs=null へ落として通常追従へ復帰する。
    // ユーザー手動操作（userOverride）が立った場合は補間を即中断（手動優先）。
    if (panKfs !== null) {
      if (userOverride) {
        panKfs = null;
      } else {
        panElapsed += dt;
        if (panElapsed >= panDuration) {
          // 補間完了: 終点を採用してから panKfs を解除（通常追従に戻る）。
          const last = panKfs[panKfs.length - 1]!;
          camera.position.set(last.pos.x, last.pos.y, last.pos.z);
          camera.lookAt(last.lookAt.x, last.lookAt.y, last.lookAt.z);
          lastRenderPos.set(last.pos.x, last.pos.y, last.pos.z);
          lastRenderTarget.set(last.lookAt.x, last.lookAt.y, last.lookAt.z);
          panKfs = null;
          return;
        }
        // easeInOutCubic で 0..1 の正規化時間 → 区間内で位置/lookAt を線形補間
        // （複数キーフレームを連結したパスとして扱う）。
        const tNorm = panElapsed / panDuration;
        const eased =
          tNorm < 0.5
            ? 4 * tNorm * tNorm * tNorm
            : 1 - Math.pow(-2 * tNorm + 2, 3) / 2;
        const segCount = panKfs.length - 1; // 区間数
        const segFloat = eased * segCount;
        const segIdx = Math.min(Math.floor(segFloat), segCount - 1);
        const localT = segFloat - segIdx;
        const a = panKfs[segIdx]!;
        const b = panKfs[segIdx + 1]!;
        const px = a.pos.x + (b.pos.x - a.pos.x) * localT;
        const py = a.pos.y + (b.pos.y - a.pos.y) * localT;
        const pz = a.pos.z + (b.pos.z - a.pos.z) * localT;
        const lx = a.lookAt.x + (b.lookAt.x - a.lookAt.x) * localT;
        const ly = a.lookAt.y + (b.lookAt.y - a.lookAt.y) * localT;
        const lz = a.lookAt.z + (b.lookAt.z - a.lookAt.z) * localT;
        camera.position.set(px, py, pz);
        camera.lookAt(lx, ly, lz);
        lastRenderPos.set(px, py, pz);
        lastRenderTarget.set(lx, ly, lz);
        return;
      }
    }

    // 狙撃カム（shot freeze）: 凍結中は snapshot 値を完全採用。
    // 解除イージング中は freezeReleaseBlend を 1→0 へ減衰させて snapshot ↔ 通常追従を線形補間。
    if (shotFrozen) {
      // 通常追従値は計算済（自動演出が「直前のまま自走」しないように blend は更新済）。
      // 出力のみ snapshot で固定する。
      camera.position.copy(freezeCamPos);
      camera.lookAt(freezeTarget);
      lastRenderPos.copy(freezeCamPos);
      lastRenderTarget.copy(freezeTarget);
    } else if (freezeReleaseBlend > 0) {
      // 解除イージング中
      if (freezeReleaseDuration > 0) {
        freezeReleaseBlend -= dt / freezeReleaseDuration;
      } else {
        freezeReleaseBlend = 0;
      }
      if (freezeReleaseBlend < 0) freezeReleaseBlend = 0;
      // smoothstep 風の easeInOutCubic
      const t = freezeReleaseBlend;
      const k = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const px = freezeCamPos.x * k + normalX * (1 - k);
      const py = freezeCamPos.y * k + normalY * (1 - k);
      const pz = freezeCamPos.z * k + normalZ * (1 - k);
      const tx = freezeTarget.x * k + fx * (1 - k);
      const ty = freezeTarget.y * k + fy * (1 - k);
      const tz = freezeTarget.z * k + fz * (1 - k);
      camera.position.set(px, py, pz);
      camera.lookAt(tx, ty, tz);
      lastRenderPos.set(px, py, pz);
      lastRenderTarget.set(tx, ty, tz);
    } else {
      // 通常追従
      camera.position.set(normalX, normalY, normalZ);
      camera.lookAt(fx, fy, fz);
      lastRenderPos.set(normalX, normalY, normalZ);
      lastRenderTarget.set(fx, fy, fz);
    }
  };

  return {
    orbit,
    zoom,
    alignToAim,
    setAimTarget,
    setFraming,
    setCinema,
    setOverview,
    setFollow,
    setFollowFrame,
    setTopViewLocked,
    setAutoTopView,
    isAutoTopActive,
    setTopFitExtents,
    topPinch,
    topPan,
    resetTopZoom,
    isTopView,
    getTopZoom,
    setOrbitTarget,
    clearOrbitTarget,
    setRadius,
    setInitialRadius,
    getRadius,
    getAzimuth,
    zoomBy,
    isUserOverridden,
    clearUserOverride,
    freezeForShot,
    releaseFreeze,
    isShotFrozen,
    restoreDefault,
    reset,
    setElevation,
    panToKeyframes,
    isPanning,
    smoothCutTo,
    setSlowMotion,
    setBroadcastAzimuthLock,
    snapInternalState,
    update,
  };
};
